import { motion } from "framer-motion";
import { Sparkles, Heart, Leaf, HandHeart } from "lucide-react";

const items = [
    {
        n: "01",
        title: "Origine France",
        text: "On privilégie les fabricants français : un savoir-faire local, des matières belles et durables, et la fierté de soutenir nos artisans.",
        bg: "bg-lpl-terracotta",
        fg: "text-lpl-bg",
        Icon: Sparkles,
        size: "lg:col-span-8",
        titleSize: "text-4xl md:text-5xl",
        wide: true,
    },
    {
        n: "02",
        title: "Petits créateurs",
        text: "On cherche les pépites : ateliers de trois personnes, marques qui osent, séries limitées et coups de cœur du monde entier.",
        bg: "bg-lpl-lavender",
        fg: "text-lpl-text",
        Icon: Heart,
        size: "lg:col-span-4 lg:row-span-2",
        titleSize: "text-3xl md:text-5xl",
        tall: true,
    },
    {
        n: "03",
        title: "Écoresponsable",
        text: "Matériaux recyclés, circuits courts. Plus léger pour la planète.",
        bg: "bg-lpl-yellow",
        fg: "text-lpl-text",
        Icon: Leaf,
        size: "lg:col-span-4",
        titleSize: "text-2xl md:text-3xl",
    },
    {
        n: "04",
        title: "Conseils sur mesure",
        text: "Le bon verre, la bonne monture, pour votre vie. On prend le temps.",
        bg: "bg-lpl-primary-deep",
        fg: "text-lpl-bg",
        Icon: HandHeart,
        size: "lg:col-span-4",
        titleSize: "text-2xl md:text-3xl",
    },
];

export default function Valeurs() {
    return (
        <section
            id="valeurs"
            className="relative py-24 md:py-32 bg-lpl-bg"
            data-testid="valeurs-section"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-16">
                    <div className="max-w-2xl">
                        <h2
                            className="font-display font-semibold text-5xl md:text-7xl text-lpl-text leading-[0.95] tracking-tight"
                            data-testid="valeurs-title"
                        >
                            Quatre obsessions{" "}
                            <span className="font-serif italic font-normal text-lpl-primary-deep">
                                bien à nous
                            </span>
                            <span className="font-serif italic font-normal text-lpl-lavender">
                                .
                            </span>
                        </h2>
                    </div>
                    <p className="lg:max-w-sm text-lpl-text-muted text-base md:text-lg leading-relaxed">
                        Être indépendante, c'est choisir. Voilà nos partis pris,
                        ceux qui font la différence quand vous poussez la porte.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-5 lg:auto-rows-[230px]">
                    {items.map((it, i) => {
                        const Icon = it.Icon;
                        return (
                            <motion.article
                                key={it.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.55,
                                    delay: i * 0.08,
                                    ease: [0.2, 0.8, 0.2, 1],
                                }}
                                className={`${it.bg} ${it.fg} ${it.size} relative overflow-hidden p-7 md:p-9 ${
                                    it.wide
                                        ? "md:flex md:items-center md:justify-between md:gap-10"
                                        : "flex flex-col gap-6 min-h-[230px]"
                                } group hover:translate-y-[-4px] transition-transform duration-300`}
                                data-testid={`valeur-card-${i}`}
                            >
                                {/* Number at top-right for wide card */}
                                {it.wide && (
                                    <span className="absolute top-7 right-7 font-serif italic text-3xl md:text-4xl opacity-80 leading-none">
                                        {it.n}
                                    </span>
                                )}

                                {it.wide ? (
                                    <>
                                        <div className="flex items-start gap-5 md:flex-1">
                                            <Icon
                                                size={40}
                                                strokeWidth={1.5}
                                                className="shrink-0 mt-1"
                                            />
                                            <div>
                                                <h3
                                                    className={`font-display font-semibold ${it.titleSize} leading-[1] tracking-tight`}
                                                >
                                                    {it.title}
                                                </h3>
                                            </div>
                                        </div>
                                        <p className="mt-5 md:mt-0 md:max-w-md text-base md:text-lg leading-relaxed opacity-95">
                                            {it.text}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-start justify-between">
                                            <Icon
                                                size={it.tall ? 44 : 32}
                                                strokeWidth={1.5}
                                                className="opacity-90"
                                            />
                                            <span className="font-serif italic text-3xl md:text-4xl opacity-80 leading-none">
                                                {it.n}
                                            </span>
                                        </div>
                                        <div>
                                            <h3
                                                className={`font-display font-semibold ${it.titleSize} leading-[0.95] tracking-tight mb-3`}
                                            >
                                                {it.title}
                                            </h3>
                                            <p className={`text-base md:text-lg leading-relaxed opacity-95 max-w-md ${it.clamp ? "line-clamp-2" : ""}`}>
                                                {it.text}
                                            </p>
                                        </div>
                                    </>
                                )}
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
